import $ from 'jquery';
import { getUser } from '~src/user';
import { createFeaturePreference } from '~graphql/controllers/createFeaturePreference';
import { fetchDisabledUserFeatures } from '~graphql/controllers/fetchFeatures';
import { updateFeaturePreference } from '~graphql/controllers/updateFeaturePreference';
import { isFeatureEnabled } from '~src/features';

const SELECTORS = {
  trigger: '#dashboard-sidebar-profile-trigger',
  menu: '#dashboard-sidebar-profile-menu',
  initialsButton: '#dashboard-sidebar-profile-initials',
  initialsMenu: '#dashboard-sidebar-profile-menu-initials',
  orgMenu: '#dashboard-sidebar-profile-menu-org',
  nameMenu: '#dashboard-sidebar-profile-menu-name',
  emailMenu: '#dashboard-sidebar-profile-menu-email',
  refresh: '#dashboard-sidebar-profile-refresh',
  returnPrev: '#dashboard-sidebar-profile-return-prev',
};

const getRouteScope = () => $('.editor-route:visible');

const getInitials = (user) => {
  const first = user.first_name?.[0] || '';
  const last = user.last_name?.[0] || '';

  if (first || last) {
    return `${first}${last}`.toUpperCase();
  }

  return (user.username?.substring(0, 2) || '--').toUpperCase();
};

const closeProfileMenu = () => {
  const $scope = getRouteScope();
  const $menu = $scope.find(SELECTORS.menu);
  const $trigger = $scope.find(SELECTORS.trigger);

  $menu.removeClass('is-open');
  $menu.attr('aria-hidden', 'true');
  $('body').removeClass('sidebar-context-locked');
  $trigger.attr('aria-expanded', 'false');
};

const openProfileMenu = ({ forceBottom = false, forceTop = false } = {}) => {
  const $scope = getRouteScope();
  const $menu = $scope.find(SELECTORS.menu);
  const $trigger = $scope.find(SELECTORS.trigger);

  if (!$menu.length || !$trigger.length) {
    return;
  }

  $menu.addClass('is-open');
  $menu.attr('aria-hidden', 'false');
  $('body').addClass('sidebar-context-locked');
  $trigger.attr('aria-expanded', 'true');

  const triggerRect = $trigger[0]?.getBoundingClientRect?.() || { bottom: 0 };
  const menuHeight = $menu.outerHeight() || 0;
  const viewportHeight = window.innerHeight;

  $menu.removeClass(
    'dashboard-sidebar-profile-menu--top dashboard-sidebar-profile-menu--bottom'
  );

  let placement = 'top';

  if (forceBottom) {
    placement = 'bottom';
  } else if (forceTop) {
    placement = 'top';
  } else {
    const shouldOpenUpwards = triggerRect.bottom + menuHeight > viewportHeight;
    placement = shouldOpenUpwards ? 'top' : 'bottom';
  }

  if (placement === 'top') {
    $menu.addClass('dashboard-sidebar-profile-menu--top');
  } else {
    $menu.addClass('dashboard-sidebar-profile-menu--bottom');
  }
};

const toggleProfileMenu = () => {
  const $menu = getRouteScope().find(SELECTORS.menu);
  if ($menu.hasClass('is-open')) {
    closeProfileMenu();
  } else {
    const forceBottom = window.innerWidth <= 992;
    openProfileMenu({ forceBottom });
  }
};

const bindProfileMenuEvents = () => {
  $('body')
    .off('click', SELECTORS.trigger)
    .on('click', SELECTORS.trigger, (event) => {
      event.preventDefault();
      toggleProfileMenu();
    });

  $('body')
    .off('keydown.homeSidebarProfile')
    .on('keydown.homeSidebarProfile', (event) => {
      if (event.key === 'Escape') {
        closeProfileMenu();
      }
    });

  // Close when clicking outside the menu/trigger
  $('body')
    .off('click.homeSidebarProfileOutside')
    .on('click.homeSidebarProfileOutside', (event) => {
      const $scope = getRouteScope();
      const $menu = $scope.find(SELECTORS.menu);

      // If menu is not open, ignore
      if (!$menu.hasClass('is-open')) return;

      const $target = $(event.target);
      const clickedInsideMenu = $target.closest(SELECTORS.menu).length > 0;
      const clickedOnTrigger = $target.closest(SELECTORS.trigger).length > 0;

      if (!clickedInsideMenu && !clickedOnTrigger) {
        closeProfileMenu();
      }
    });
};

const populateProfileContext = (user) => {
  const orgName = user.org_pointer?.nome || '';
  const fullName =
    user.first_name && user.last_name
      ? `${user.first_name} ${user.last_name}`
      : user.username;
  const email = user.username || '';
  const initials = getInitials(user);

  const $scopes = $('.editor-route');
  $scopes.each((_, el) => {
    const $scope = $(el);
    $scope.find(SELECTORS.initialsButton).text(initials);
    $scope.find(SELECTORS.initialsMenu).text(initials);
    $scope.find(SELECTORS.orgMenu).text(orgName);
    $scope.find(SELECTORS.nameMenu).text(fullName);
    $scope.find(SELECTORS.emailMenu).text(email);
  });

  $('#sidebar-profile-org').text(orgName);
  $('#sidebar-profile-user').text(fullName);
};

const bindRefreshAction = () => {
  $('body')
    .off('click', SELECTORS.refresh)
    .on('click', SELECTORS.refresh, async (event) => {
      event.preventDefault();

      const $button = $(event.currentTarget);
      if ($button.data('loading')) {
        return;
      }

      try {
        $button.data('loading', true);
        $button.addClass('is-loading');
        const $label = $button.find('span').last();
        const previousLabel = $label.text();
        $label.data('previous', previousLabel);
        $label.text('Aggiornamento…');

        const startup = await getUser({}, true);
        if (startup?.user) {
          populateProfileContext(startup.user);
        }
      } catch (error) {
        console.error('Aggiornamento configurazioni non riuscito', error);
      } finally {
        const $label = $button.find('span').last();
        const previousLabel = $label.data('previous');
        if (previousLabel) {
          $label.text(previousLabel);
        }
        $button.removeClass('is-loading');
        $button.data('loading', false);
        closeProfileMenu();
      }
    });

  $('body')
    .off('click', '#dashboard-sidebar-profile-nav')
    .on('click', '#dashboard-sidebar-profile-nav', () => {
      closeProfileMenu();
    });

  $('body')
    .off('click', SELECTORS.returnPrev)
    .on('click', SELECTORS.returnPrev, async (event) => {
      event.preventDefault();
      const $button = $(event.currentTarget);
      if ($button.data('loading')) return;

      try {
        $button.data('loading', true);
        $button.addClass('is-loading');
        const $label = $button.find('span').last();
        const previousLabel = $label.text();
        $label.data('previous', previousLabel);
        $label.text('Aggiornamento…');

        // Update existing user preference for giotto.use_v2 if present, else create
        const { user } = await getUser();
        const orgId = user?.org_pointer?.objectId;
        const userId = user?.objectId;
        let updated = false;
        if (orgId && userId) {
          try {
            const prefs = await fetchDisabledUserFeatures(orgId, userId);
            const existing = (prefs || []).find(
              (p) => p.key === 'giotto.use_v2' && p.product === 'giotto'
            );
            if (existing?.objectId) {
              await updateFeaturePreference({
                id: existing.objectId,
                disabled: true,
              });
              updated = true;
            }
          } catch (e) {
            console.warn('Fetch existing feature preference failed', e);
          }
        }

        if (!updated) {
          await createFeaturePreference({
            key: 'giotto.use_v2',
            disabled: true,
            product: 'giotto',
          });
        }

        closeProfileMenu();
        window.location.href =
          'https://editor.giotto.onoranzefunebricloud.com/start?ref=return-from-beta';
      } catch (error) {
        console.error(
          'Impossibile aggiornare la preferenza giotto.use_v2',
          error
        );
      } finally {
        const $label = $button.find('span').last();
        const previousLabel = $label.data('previous');
        if (previousLabel) $label.text(previousLabel);
        $button.removeClass('is-loading');
        $button.data('loading', false);
      }
    });
};

export const initSidebarProfile = async () => {
  const $trigger = $(SELECTORS.trigger);
  if (!$trigger.length) {
    return;
  }

  try {
    const { user } = await getUser();
    if (user) {
      populateProfileContext(user);
    }

    // Show "Ritorna alla versione precedente" only if giotto.use_v2 is enabled
    try {
      const useV2Enabled = await isFeatureEnabled('giotto.use_v2');
      const $returnBtn = $(SELECTORS.returnPrev);
      if (useV2Enabled) {
        $returnBtn.show();
      } else {
        $returnBtn.hide();
      }
    } catch (err) {
      console.error('Errore durante la verifica di giotto.use_v2', err);
      $(SELECTORS.returnPrev).hide();
    }
  } catch (error) {
    console.error('Impossibile recuperare i dati utente', error);
  }

  bindProfileMenuEvents();
  bindRefreshAction();
};
